import {
    ClientCampaignInstagramPostStats,
    ClientCampaignInstagramPostStatsTotals,
    getClientCampaignPostInstagramStats,
} from '@round/api';
import { useCallback, useState } from 'react';
import { showNotification } from '../../../../../utils/showNotification';

export default function useCampaignInstagramPostStats(campaignId: number | null, instagramCreatorsCount: number) {
    const [postStats, setPostStats] = useState<ClientCampaignInstagramPostStats[]>([]);
    const [postStatsTotals, setPostStatsTotals] = useState<ClientCampaignInstagramPostStatsTotals | null>(null);

    const [isLoading, setIsLoading] = useState(false);
    const [hasErrorLoading, setHasErrorLoading] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);

    const fetchPostStats = useCallback(
        async (campaignId: number, instagramCreatorsCount: number, requestInit?: RequestInit) => {
            try {
                if (instagramCreatorsCount <= 0) {
                    return;
                }
                setHasErrorLoading(false);
                setIsLoading(true);
                const response = await getClientCampaignPostInstagramStats(
                    {
                        campaignId,
                        page_size: instagramCreatorsCount,
                    },
                    requestInit
                );

                if (response.status === 404) {
                    throw new Error(response.data.detail);
                }

                setPostStats(response.data.results);
                setPostStatsTotals(response.data.totals);
            } catch (e) {
                if (e instanceof Error && e.name === 'AbortError') {
                    return;
                }

                setHasErrorLoading(true);
                showNotification((e as Error).message, 'error');
                throw e;
            } finally {
                setIsLoading(false);
            }
        },
        []
    );

    const init = useCallback(
        (requestInit?: RequestInit) => {
            if (typeof campaignId === 'number') {
                fetchPostStats(campaignId, instagramCreatorsCount, requestInit).then(() => setIsInitialized(true));
            }
        },
        [campaignId, fetchPostStats, instagramCreatorsCount]
    );

    return {
        postStats,
        postStatsTotals,
        isLoading,
        hasErrorLoading,
        isInitialized,
        init,
    };
}
